import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, FileIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { Employee, ProCustomer, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { INVOICE_STATUS, JOB_STATUS, QUOTE_STATUS, formatDate, invoiceTotal, kr, quoteTotal } from '../../utils/pro';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProCustomerDetailProps {
  customer: ProCustomer;
  jobs: ProJob[];
  quotes: Quote[];
  invoices: ProInvoice[];
  timeEntries: TimeEntry[];
  employees: Employee[];
  onBack: () => void;
  onOpenJob: (jobId: string) => void;
  onOpenQuote: (quoteId: string) => void;
  onUpdate: (changes: Partial<ProCustomer>) => void;
}
type Tab = 'overblik' | 'opgaver' | 'tilbud' | 'fakturaer' | 'timer' | 'dokumenter' | 'noter';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'overblik',
  label: 'Overblik'
}, {
  id: 'opgaver',
  label: 'Opgaver'
}, {
  id: 'tilbud',
  label: 'Tilbud'
}, {
  id: 'fakturaer',
  label: 'Fakturaer'
}, {
  id: 'timer',
  label: 'Timer'
}, {
  id: 'dokumenter',
  label: 'Dokumenter'
}, {
  id: 'noter',
  label: 'Noter'
}];
export function ProCustomerDetail({
  customer,
  jobs,
  quotes,
  invoices,
  timeEntries,
  employees,
  onBack,
  onOpenJob,
  onOpenQuote,
  onUpdate
}: ProCustomerDetailProps) {
  const [tab, setTab] = useState<Tab>('overblik');
  const own = useMemo(() => ({
    jobs: jobs.filter(job => job.customerId === customer.id),
    quotes: quotes.filter(quote => quote.customerId === customer.id),
    invoices: invoices.filter(invoice => invoice.customerId === customer.id)
  }), [jobs, quotes, invoices, customer.id]);
  const jobIds = own.jobs.map(job => job.id);
  const hours = timeEntries.filter(entry => jobIds.includes(entry.jobId));
  const totalHours = hours.reduce((sum, entry) => sum + entry.hours, 0);
  const revenue = own.invoices.filter(invoice => invoice.status !== 'kladde').reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
  const timeline = useMemo(() => {
    const entries: {
      id: string;
      date: string;
      text: string;
    }[] = [];
    own.quotes.forEach(quote => entries.push({
      id: `q-${quote.id}`,
      date: quote.createdDate,
      text: `Tilbud ${quote.quoteNo} — ${QUOTE_STATUS[quote.status].label} · ${kr(quoteTotal(quote))}`
    }));
    own.jobs.forEach(job => entries.push({
      id: `j-${job.id}`,
      date: job.start ?? '',
      text: `Opgave ${job.jobNo} — ${job.title} · ${JOB_STATUS[job.status].label}`
    }));
    own.invoices.forEach(invoice => entries.push({
      id: `i-${invoice.id}`,
      date: invoice.invoiceDate,
      text: `Faktura ${invoice.invoiceNo} — ${INVOICE_STATUS[invoice.status].label} · ${kr(invoiceTotal(invoice))}`
    }));
    return entries.filter(entry => entry.date).sort((a, b) => b.date.localeCompare(a.date));
  }, [own]);
  const card = 'rounded-xl border border-stone-200 bg-white';
  return <div data-id="element-657" data-mp="22qarv1v09wkw">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-658" data-mp="22pj94rmibtc1">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-659" data-mp="2cfucod2a40ie" />
        Alle kunder
      </button>

      <header className="mb-5" data-id="element-660" data-mp="2hfh0fgfqgz5g">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand-700" data-id="element-661" data-mp="2henp025txlgz">
          {customer.customerNo}
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-662" data-mp="2lfhhz9y4pqgq">
          {customer.name}
        </h1>
        <p className="mt-1 text-sm text-stone-500" data-id="element-663" data-mp="r3sz4wos13r2">
          {customer.address}, {customer.postalCode} {customer.city}
        </p>
      </header>

      <div role="tablist" aria-label="Kundeoplysninger" className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-664" data-mp="w35rty2bx51k">
        {TABS.map(item => <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-665" data-mp="3s0uzgy596ygf">
            {item.label}
          </button>)}
      </div>

      {tab === 'overblik' && <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-666" data-mp="3j6glr00dfvzf">
          <div className="space-y-5 lg:col-span-2" data-id="element-667" data-mp="3j4voh2mo9yxv">
            <section className={`${card} grid grid-cols-2 gap-px overflow-hidden bg-stone-200 lg:grid-cols-4`} data-id="element-668" data-mp="2vgchqrj9tu7w">
              {[{
            label: 'Opgaver',
            value: own.jobs.length
          }, {
            label: 'Tilbud',
            value: own.quotes.length
          }, {
            label: 'Registrerede timer',
            value: `${totalHours.toLocaleString('da-DK')} t`
          }, {
            label: 'Omsætning',
            value: kr(revenue)
          }].map(stat => <div key={stat.label} className="bg-white p-4" data-id="element-669" data-mp="111fg2gsx9p7c">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-670" data-mp="1oqhyvrzalo3f">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-stone-900" data-id="element-671" data-mp="1tojoywv4ynoe">
                    {stat.value}
                  </p>
                </div>)}
            </section>

            <section className={`${card} overflow-hidden`} data-id="element-672" data-mp="1jpc31lac5kpo">
              <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-673" data-mp="4a53qmjbtixn">
                <h2 className="text-base font-semibold text-stone-900" data-id="element-674" data-mp="8bqeo2cavn57">
                  Historik
                </h2>
              </header>
              {timeline.length === 0 ? <EmptyState title="Ingen historik endnu" data-id="element-675" data-mp="2b0hwyke3g5rd" /> : <ol className="divide-y divide-stone-100" data-id="element-676" data-mp="260carjqlfbgo">
                  {timeline.map(entry => <li key={entry.id} className="flex items-start gap-3 px-5 py-3" data-id="element-677" data-mp="216ra1jxm83ds">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-stone-300" data-id="element-678" data-mp="1s8fmxrvyjnq3" />
                      <span className="min-w-0 flex-1 text-sm text-stone-700" data-id="element-679" data-mp="1n8t85e0hdzf6">
                        {entry.text}
                      </span>
                      <span className="flex-shrink-0 text-xs text-stone-400" data-id="element-680" data-mp="1ic2lg7u685ks">
                        {formatDate(entry.date)}
                      </span>
                    </li>)}
                </ol>}
            </section>
          </div>

          <section className={`${card} p-5`} data-id="element-681" data-mp="37q7mjb7m9xdr">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-682" data-mp="3g5ww9nec2mw6">
              Kontakt
            </h2>
            <ul className="space-y-3 text-sm" data-id="element-683" data-mp="3k7a1bv9lqm21">
              {customer.contactPerson && <li data-id="element-684" data-mp="3oyie91a54pjy">
                  <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-685" data-mp="3oxyvjico1x4v">
                    Kontaktperson
                  </span>
                  <span className="text-stone-900" data-id="element-686" data-mp="3tzfs51o4axfn">
                    {customer.contactPerson}
                  </span>
                </li>}
              <li data-id="element-687" data-mp="319pcgo6u7nhf">
                <a href={`tel:${customer.phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-688" data-mp="31965g2iq6ji6">
                  <PhoneIcon className="h-4 w-4 text-stone-400" data-id="element-689" data-mp="3b4r1zl50yrkj" />
                  {customer.phone}
                </a>
              </li>
              <li data-id="element-690" data-mp="qg3d0ipzxwtm">
                <a href={`mailto:${customer.email}`} className="flex items-center gap-2.5 break-all text-stone-700 transition-colors hover:text-brand-700" data-id="element-691" data-mp="qfjv320cj8fl">
                  <MailIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-692" data-mp="17kd56855g9rw" />
                  {customer.email}
                </a>
              </li>
              <li data-id="element-693" data-mp="1gjqjpf84rojh">
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${customer.address}, ${customer.postalCode} ${customer.city}`)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-694" data-mp="1gj8brlc3vlvq">
                  <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-695" data-mp="1z9ztba68yil7" />
                  <span data-id="element-696" data-mp="9urylr43ga8m">
                    {customer.address}
                    <br data-id="element-697" data-mp="9xe7pv6muxsy" />
                    {customer.postalCode} {customer.city}
                  </span>
                </a>
              </li>
              {customer.leadSource && <li data-id="element-698" data-mp="2gkc2m1vy4q7y">
                  <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-699" data-mp="2glwq57juf8wb">
                    Kom fra
                  </span>
                  <span className="text-stone-900" data-id="element-700" data-mp="27q77h69s4smm">{customer.leadSource}</span>
                </li>}
              <li data-id="element-701" data-mp="2brixve0h9hvv">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-702" data-mp="2br0rhok3pna8">
                  Kunde siden
                </span>
                <span className="text-stone-900" data-id="element-703" data-mp="22sov15wjzv9v">
                  {formatDate(customer.createdDate)}
                </span>
              </li>
            </ul>
          </section>
        </div>}

      {tab === 'opgaver' && <section className={`${card} overflow-hidden`} data-id="element-704" data-mp="1sur7kkmiq2ib">
          {own.jobs.length === 0 ? <EmptyState title="Ingen opgaver på denne kunde" data-id="element-705" data-mp="3j7qgjfa1jpt3" /> : <ul className="divide-y divide-stone-100" data-id="element-706" data-mp="3n8acwgmsipe6">
              {own.jobs.map(job => <li key={job.id} data-id="element-707" data-mp="31bjd2d8nw9h0">
                  <button onClick={() => job.id && onOpenJob(job.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-708" data-mp="31agls47fg50k">
                    <span className="min-w-0 flex-1" data-id="element-709" data-mp="2rc0sbukxgjnw">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-710" data-mp="2wdq4xenst5e3">
                        {job.title}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-711" data-mp="3p1dlmily7bvg">
                        {job.jobNo} · {formatDate(job.start ?? '')}
                      </span>
                    </span>
                    <StatusChip meta={JOB_STATUS[job.status]} data-id="element-712" data-mp="3f3fwzu06fd2b" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'tilbud' && <section className={`${card} overflow-hidden`} data-id="element-713" data-mp="23o14b2f037d0">
          {own.quotes.length === 0 ? <EmptyState title="Ingen tilbud på denne kunde" data-id="element-714" data-mp="1w4i11aibwhcz" /> : <ul className="divide-y divide-stone-100" data-id="element-715" data-mp="1w6l0sm9a9ouw">
              {own.quotes.map(quote => <li key={quote.id} data-id="element-716" data-mp="1r81cif77w0zd">
                  <button onClick={() => quote.id && onOpenQuote(quote.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-717" data-mp="2637kh7pvi9vc">
                    <span className="min-w-0 flex-1" data-id="element-718" data-mp="1dejuwzy3cybz">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-719" data-mp="18fpc3y04j9rv">
                        {quote.title}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-720" data-mp="1n66f3lseq7qv">
                        {quote.quoteNo} · {formatDate(quote.createdDate)}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-721" data-mp="uloamwhsf8nt">
                      {kr(quoteTotal(quote))}
                    </span>
                    <StatusChip meta={QUOTE_STATUS[quote.status]} data-id="element-722" data-mp="1fbs5x5qrkjoj" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'fakturaer' && <section className={`${card} overflow-hidden`} data-id="element-723" data-mp="1o5os2kjxhw3y">
          {own.invoices.length === 0 ? <EmptyState title="Ingen fakturaer på denne kunde" data-id="element-724" data-mp="1t3qd3b0x05i2" /> : <ul className="divide-y divide-stone-100" data-id="element-725" data-mp="wf0qj7eceopr">
              {own.invoices.map(invoice => <li key={invoice.id} className="flex items-center gap-4 px-5 py-3.5" data-id="element-726" data-mp="10gcbv0q6ons5">
                  <span className="min-w-0 flex-1" data-id="element-727" data-mp="15h0ao8knm8wx">
                    <span className="block text-sm font-medium text-stone-900" data-id="element-728" data-mp="1aejoj7dfwjoc">
                      {invoice.invoiceNo}
                    </span>
                    <span className="block text-xs text-stone-500" data-id="element-729" data-mp="2lsx8i152oez3">
                      Forfalder {formatDate(invoice.dueDate)}
                    </span>
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-730" data-mp="a7mxo7yymagv">
                    {kr(invoiceTotal(invoice))}
                  </span>
                  <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-731" data-mp="epbnrure3di" />
                </li>)}
            </ul>}
        </section>}

      {tab === 'timer' && <section className={`${card} overflow-hidden`} data-id="element-732" data-mp="3nkqckrynr7xw">
          {hours.length === 0 ? <EmptyState title="Ingen registrerede timer" data-id="element-733" data-mp="3iku7sqi4zcha" /> : <>
              <ul className="divide-y divide-stone-100" data-id="element-734" data-mp="3dm0x15u8hzkt">
                {hours.map(entry => {
            const job = own.jobs.find(j => j.id === entry.jobId);
            const employee = employees.find(e => e.id === entry.employeeId);
            return <li key={entry.id} className="flex items-center gap-4 px-5 py-3" data-id="element-735" data-mp="16ww0lnvja7l3">
                      <span className="min-w-0 flex-1" data-id="element-736" data-mp="1jvlmgahbpas5">
                        <span className="block truncate text-sm text-stone-900" data-id="element-737" data-mp="1juafy2whtoxc">
                          {job?.title ?? 'Opgave'}
                        </span>
                        <span className="block text-xs text-stone-500" data-id="element-738" data-mp="19wweb9f8b4p3">
                          {employee?.name ?? 'Ukendt'} · {entry.workType} ·{' '}
                          {formatDate(entry.date)}
                        </span>
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-739" data-mp="10yu7uecq7juf">
                        {entry.hours.toLocaleString('da-DK')} t
                      </span>
                    </li>;
          })}
              </ul>
              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-5 py-3" data-id="element-740" data-mp="d8824tq72bnl">
                <span className="text-sm font-medium text-stone-600" data-id="element-741" data-mp="h88pj3v9tx6s">
                  I alt
                </span>
                <span className="text-sm font-bold tabular-nums text-stone-900" data-id="element-742" data-mp="3uryebxf6izqu">
                  {totalHours.toLocaleString('da-DK')} timer
                </span>
              </div>
            </>}
        </section>}

      {tab === 'dokumenter' && <EmptyState icon={FileIcon} title="Ingen dokumenter" description="Tegninger, aftaler og rapporter samles her når de lægges på en opgave." data-id="element-743" data-mp="2l9zndc2bau6" />}

      {tab === 'noter' && <section className={`${card} p-5`} data-id="element-744" data-mp="cjz9qjxpvqzv">
          <label className="block" data-id="element-745" data-mp="qc5l7spxd9sz">
            <span className="mb-2 block text-sm font-medium text-stone-700" data-id="element-746" data-mp="qb4bpsok4vdd">
              Interne noter om kunden
            </span>
            <textarea value={customer.notes} onChange={e => onUpdate({
          notes: e.target.value
        })} rows={8} placeholder="Adgangsforhold, aftaler, hvad man skal huske…" className="w-full rounded-xl border border-stone-200 px-4 py-3 text-sm leading-relaxed outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-747" data-mp="mceqjdxa29j6" />
          </label>
        </section>}
    </div>;
}